'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuthUser } from '@/hooks/useAuthUser';
import profileServices from '@/services/profile-services';

/**
 * User-controlled opt-out for the Profil+ featured slot (issue #3687,
 * S1 soft requirement of #3663).
 *
 * Persisted in localStorage so the preference survives across sessions
 * on the same device. Default is `false` (slot visible). For *signed-in*
 * users the preference is additionally persisted server-side
 * (`users.hide_featured_slot`, #5300) so it follows them across browsers
 * and devices — see `useHideFeaturedSlotControl` below. localStorage stays
 * the synchronous render cache (and the only store for anonymous users).
 *
 * Why this exists: zákon č. 108/2024 + EU UCPD give consumers the right
 * to control their exposure to paid online-marketplace placements. A
 * one-click opt-out is the strongest defence against any "manipulative
 * advertising" complaint — the user had to actively choose to keep the
 * slot visible (or to hide it) and that choice is recorded.
 *
 * Pattern mirrors `useDevMode`: a custom `featured-slot:toggle` event
 * lets sibling tabs and components on the same page react to a change
 * without polling localStorage.
 */

const STORAGE_KEY = 'lexomat_hide_featured_slot';
const TOGGLE_EVENT = 'featured-slot:toggle';

const readStored = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY) === 'true';
};

// Module-level guard: at most ONE profile fetch per signed-in user per page
// load, even when several `useHideFeaturedSlotControl` consumers mount at once
// (e.g. up to 3 FeaturedSlots in a chat-match result). Reset on fetch failure
// so a later mount can retry, and naturally re-fires when the userId changes
// (sign-out → different sign-in).
let reconciledUserId: string | null = null;

/** Returns `true` when the user has opted out of seeing the featured slot.
 *
 * SSR-safe two-pass pattern (issue #3718 sub-3): initial state is always
 * `false` so server-rendered HTML matches the first client render — no
 * hydration mismatch warning, no SSR/CSR flash for opted-out users. The
 * useEffect-mounted listener then reads localStorage and updates state on
 * the next commit. Cost: opted-out users see one render with the slot
 * before it disappears, which is acceptable. Hydration warning would be
 * worse (logged to prod console + React 19 may bail to client render). */
export function useHideFeaturedSlot(): boolean {
  const [hidden, setHidden] = useState<boolean>(false);

  useEffect(() => {
    // First pass: read the persisted value once after mount.
    setHidden(readStored());

    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setHidden(readStored());
    };
    const handleCustom = () => setHidden(readStored());

    window.addEventListener('storage', handleStorage);
    window.addEventListener(TOGGLE_EVENT, handleCustom);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(TOGGLE_EVENT, handleCustom);
    };
  }, []);

  return hidden;
}

/** Set the hidden state explicitly, or toggle if `next` is omitted.
 *
 * localStorage-only — writes the device cache and broadcasts the
 * `featured-slot:toggle` event so sibling consumers re-read. Server
 * persistence for signed-in users is layered on top by
 * `useHideFeaturedSlotControl`'s setter. */
export function setHideFeaturedSlot(next?: boolean): void {
  if (typeof window === 'undefined') return;
  const current = readStored();
  const resolved = typeof next === 'boolean' ? next : !current;
  window.localStorage.setItem(STORAGE_KEY, String(resolved));
  window.dispatchEvent(new Event(TOGGLE_EVENT));
}

interface HideFeaturedSlotControl {
  /** Current opt-out state (localStorage cache; reconciled from the server
   *  for signed-in users on mount). `true` = placements hidden. */
  hidden: boolean;
  /** Persist a new opt-out state: writes the localStorage cache (and
   *  broadcasts to sibling consumers) immediately, and for signed-in users
   *  also persists to `users.hide_featured_slot` so it syncs across devices. */
  setHidden: (next: boolean) => void;
  /** True when the preference is backed by the user's account (signed in),
   *  i.e. it syncs across devices rather than living only in this browser.
   *  Drives honest, auth-gated copy on the disclosure page + preferences. */
  isSynced: boolean;
}

/**
 * Server-aware control over the Profil+ opt-out (#5300 / #5299).
 *
 * Builds on the localStorage primitives so the synchronous render decision
 * (FeaturedSlot's `if (hidden) return null`) and cross-component broadcast
 * keep working unchanged. On top of that, for *signed-in* users it:
 *   - reconciles the server value (`users.hide_featured_slot`, the
 *     cross-device source of truth) into the localStorage cache on mount, and
 *   - mirrors every write back to the server.
 *
 * Anonymous users get the pure-localStorage behaviour (no server row), and
 * `isSynced` is false so callers can render "saved in this browser" copy
 * instead of "synced to your account".
 */
export function useHideFeaturedSlotControl(): HideFeaturedSlotControl {
  const hidden = useHideFeaturedSlot();
  const { userId } = useAuthUser();
  // A user toggle that lands while the reconcile fetch is in flight must win
  // over the (now stale) server value — flip this so the reconcile skips its
  // write rather than clobbering the fresh choice (which is already on its
  // way to the server via setHidden).
  const userTouchedRef = useRef(false);

  useEffect(() => {
    if (!userId || reconciledUserId === userId) return;
    // A new user is reconciling — clear any toggle flag carried over from a
    // previous account in this still-mounted instance, so their server value
    // isn't dropped by the prior user's "touched" guard (account switch is a
    // client-side router.push, not a reload, so the ref survives).
    userTouchedRef.current = false;
    // Claim the reconcile synchronously so concurrently-mounted consumers
    // don't each fire their own profile fetch.
    reconciledUserId = userId;
    let cancelled = false;
    profileServices
      .getUserProfile()
      .then((response) => {
        if (cancelled || userTouchedRef.current || !response.data) return;
        const serverValue = response.data.hide_featured_slot ?? false;
        if (serverValue !== readStored()) setHideFeaturedSlot(serverValue);
      })
      .catch((error) => {
        // Non-fatal: keep the localStorage value and allow a later mount to
        // retry the reconcile.
        reconciledUserId = null;
        console.error('Failed to load hide-featured-slot preference:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setHidden = useCallback(
    (next: boolean) => {
      userTouchedRef.current = true;
      setHideFeaturedSlot(next); // optimistic local cache + cross-component event
      if (!userId) return; // anonymous: localStorage is the only store
      profileServices
        .updateUserProfile({ hide_featured_slot: next })
        .catch((error) => {
          console.error(
            'Failed to persist hide-featured-slot preference:',
            error
          );
        });
    },
    [userId]
  );

  return { hidden, setHidden, isSynced: Boolean(userId) };
}
